import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, Loader2, ShieldCheck, CreditCard } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { fetchVehicles, submitRental, fetchRentalAvailability, depositCheckout, zar } from "@/api";

const today = () => new Date().toISOString().slice(0, 10);

export default function RentalBooking() {
  const [params] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({
    full_name: "", email: "", mobile: "",
    vehicle_slug: params.get("vehicle") || "",
    start_date: today(), end_date: "",
    licence_confirmed: false,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchVehicles()
      .then((vs) => setVehicles(vs.filter((v) => (v.services || []).includes("rental") && v.status !== "sold")))
      .catch(() => {});
  }, []);

  const [booked, setBooked] = useState([]);
  useEffect(() => {
    if (!form.vehicle_slug) {
      setBooked([]);
      return;
    }
    fetchRentalAvailability(form.vehicle_slug).then(setBooked).catch(() => setBooked([]));
  }, [form.vehicle_slug]);

  const overlap = booked.some((r) => form.start_date < r.end_date && form.end_date > r.start_date);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const selected = vehicles.find((v) => v.slug === form.vehicle_slug);
  const days = useMemo(() => {
    if (!form.start_date || !form.end_date) return 0;
    return Math.max(0, Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000));
  }, [form.start_date, form.end_date]);
  const estimate = selected && days > 0 ? days * Number(selected.day_rate) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (days < 1) return toast.error("Return date must be after the collection date.");
    if (overlap) return toast.error("Those dates overlap an existing booking — please pick different days.");
    setLoading(true);
    try {
      const res = await submitRental(form);
      setResult({ ...res, vehicle: selected });
      toast.success("Rental request sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — please call 010 534 9926.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="rental-page" className="pt-32 pb-24 min-h-screen">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-4">Short-term rentals</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-black text-white mb-6">
            Book a car for days, not paperwork.
          </h1>
          <p className="text-base text-slate-400 leading-relaxed mb-10">
            Pick your vehicle and dates, and we'll confirm availability the same
            business day. Debit order and EFT accepted — no credit card needed.
            Verified ID, valid licence and a refundable security deposit, and
            you're on the road.
          </p>
          <ul className="space-y-5">
            {[
              "Routine maintenance and approved insurance/waiver included",
              "Transparent day rate — no hidden kilometre games",
              "Love the car? Ask us about switching it to rent-to-own",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-slate-300">
                <ShieldCheck size={18} className="text-[#F57C00] mt-0.5 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.15}>
          <AnimatePresence mode="wait">
            {!result ? (
              <motion.form
                key="form"
                data-testid="rental-form"
                onSubmit={submit}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="bg-[#122659]/80 backdrop-blur-xl border border-sky-300/20 rounded-xl p-8 md:p-10 space-y-5"
              >
                <div>
                  <label className="input-label" htmlFor="rn-vehicle">Vehicle</label>
                  <select id="rn-vehicle" data-testid="rental-vehicle-select" className="input-field" value={form.vehicle_slug} onChange={set("vehicle_slug")}>
                    <option value="">Any available vehicle</option>
                    {vehicles.map((v) => (
                      <option key={v.slug} value={v.slug}>{v.year} {v.make} {v.model} — R{v.day_rate}/day</option>
                    ))}
                  </select>
                  {booked.length > 0 && (
                    <div className="mt-3" data-testid="rental-booked-ranges">
                      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Already booked</p>
                      <div className="flex flex-wrap gap-2">
                        {booked.map((r, i) => (
                          <span key={i} className="text-[11px] font-semibold text-red-300 bg-red-400/10 border border-red-400/30 rounded-full px-3 py-1">
                            {new Date(r.start_date).toLocaleDateString("en-ZA", { day: "numeric", month: "short" })} – {new Date(r.end_date).toLocaleDateString("en-ZA", { day: "numeric", month: "short" })}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="input-label" htmlFor="rn-start">Collection date</label>
                    <input id="rn-start" data-testid="rental-start-date" type="date" required min={today()} className="input-field" value={form.start_date} onChange={set("start_date")} />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="rn-end">Return date</label>
                    <input id="rn-end" data-testid="rental-end-date" type="date" required min={form.start_date} className="input-field" value={form.end_date} onChange={set("end_date")} />
                  </div>
                </div>

                {overlap && days > 0 && (
                  <p data-testid="rental-overlap-warning" className="text-sm text-red-300 bg-red-400/10 border border-red-400/30 rounded-md px-4 py-3">
                    These dates overlap an existing booking for this vehicle — please choose different days.
                  </p>
                )}

                {estimate > 0 && (
                  <div className="bg-[#0B1F4E] border border-[#F57C00]/40 rounded-lg p-5 flex items-center justify-between" data-testid="rental-estimate">
                    <span className="text-sm text-slate-300 flex items-center gap-2">
                      <CalendarDays size={16} className="text-[#F57C00]" />
                      {days} day{days !== 1 ? "s" : ""} × R{selected.day_rate}/day
                    </span>
                    <span className="font-display text-2xl font-black text-[#F57C00]" data-testid="rental-estimate-total">{zar(estimate)}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <label className="input-label" htmlFor="rn-name">Full name</label>
                    <input id="rn-name" data-testid="rental-input-name" required className="input-field" value={form.full_name} onChange={set("full_name")} placeholder="Thandi Mokoena" />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="rn-email">Email</label>
                    <input id="rn-email" data-testid="rental-input-email" type="email" required className="input-field" value={form.email} onChange={set("email")} placeholder="you@example.co.za" />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="rn-mobile">Mobile</label>
                    <input id="rn-mobile" data-testid="rental-input-mobile" required className="input-field" value={form.mobile} onChange={set("mobile")} placeholder="082 000 0000" />
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input type="checkbox" data-testid="rental-licence-check" checked={form.licence_confirmed} onChange={set("licence_confirmed")} className="mt-1 w-4 h-4 accent-[#F57C00]" />
                  <span className="text-sm text-slate-300 group-hover:text-white transition-colors">
                    I hold a valid, un-suspended driver's licence and can present it with my ID at collection.
                  </span>
                </label>

                <button type="submit" data-testid="rental-submit-btn" disabled={loading} className="w-full bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white font-bold px-6 py-4 rounded-md transition-colors duration-300 flex items-center justify-center gap-2">
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  {loading ? "Sending…" : "Request this rental"}
                </button>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  A request, not a final booking — we confirm availability, verification and deposit in writing first.
                </p>
              </motion.form>
            ) : (
              <motion.div
                key="done"
                data-testid="rental-success"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#122659]/80 backdrop-blur-xl border border-emerald-400/40 rounded-xl p-8 md:p-10 text-center"
              >
                <CheckCircle2 size={56} className="text-emerald-400 mx-auto mb-6" />
                <h2 className="font-display text-3xl font-black text-white mb-3">Request received.</h2>
                <p className="text-slate-400 text-sm mb-2">Your booking reference is</p>
                <p className="font-display text-2xl font-black text-[#F57C00] tracking-widest mb-6" data-testid="rental-reference">{result.reference}</p>
                <div className="bg-[#0B1F4E] rounded-lg p-5 text-sm text-slate-300 mb-8">
                  {result.vehicle ? `${result.vehicle.year} ${result.vehicle.make} ${result.vehicle.model}` : "Any available vehicle"} · {result.days} day{result.days !== 1 ? "s" : ""}
                  {result.estimated_total > 0 && <> · estimated <strong className="text-white">{zar(result.estimated_total)}</strong></>}
                </div>
                <p className="text-xs text-slate-500 mb-6">A confirmation is on its way to your email. We'll confirm availability and deposit the same business day.</p>
                <button
                  data-testid="rental-pay-deposit-btn"
                  onClick={async () => {
                    try {
                      const res = await depositCheckout(result.reference, window.location.origin);
                      window.location.href = res.checkout_url;
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Could not start online payment — you can also pay by EFT.");
                    }
                  }}
                  className="w-full bg-[#F57C00] hover:bg-[#E67300] text-white font-bold px-6 py-4 rounded-md transition-colors duration-300 mb-4 flex items-center justify-center gap-2"
                >
                  <CreditCard size={18} /> Pay R3 000 deposit securely online
                </button>
                <Link to="/vehicles" data-testid="rental-browse-more" className="text-sky-300 hover:text-white font-bold text-sm transition-colors">
                  ← Browse more vehicles
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </Reveal>
      </div>
    </div>
  );
}
